import {
  createContext,
  useContext,
  useState,
  useEffect,
  ReactNode,
} from 'react';
import { useOrchestrator } from './useOrchestrator';
import { Action } from '@/business/types/action.types';
import { ResponseCode } from '@/business/types/response-codes.types';
import type { LoginInput } from '@/types/generated';
import type { LoginUIOutput as LoginOutput, AuthUser } from '@/types/auth';
import StorageService from '@/utils/storage';
import { StorageKey } from '@/types/storage';
import { httpClient } from '@/utils/http-client';
import { logger } from '@/utils/logger';

interface AuthContextType {
  user: AuthUser | null;
  login: (
    username: string,
    password: string,
    organization_code: string
  ) => Promise<{ success: boolean; message?: string }>;
  logout: () => void;
  updateUser: (updates: Partial<AuthUser>) => void;
  isLoading: boolean;
  isAuthenticated: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  // Lazy init from storage — auth is the single owner of USER and AUTH_TOKEN.
  // Reading on first render avoids a separate mount effect.
  const [user, setUser] = useState<AuthUser | null>(() =>
    StorageService.get<AuthUser>(StorageKey.USER)
  );
  const [authToken, setAuthToken] = useState<string | null>(() =>
    StorageService.get<string>(StorageKey.AUTH_TOKEN)
  );
  const [isLoading, setIsLoading] = useState(false);
  const { execute } = useOrchestrator<LoginInput, LoginOutput>(Action.LOGIN);

  // Register the token provider with HttpClient synchronously on every render.
  // HttpClient never reads storage directly — it asks the auth owner.
  //
  // Must be synchronous (not in useEffect) because React fires children's
  // effects before parents'. On page refresh, child components (e.g. KanbanBoard)
  // would submit their first API call before a useEffect-based registration
  // could run, sending requests without an auth header.
  //
  // Fallback to storage handles a specific race: the LOGIN executeSideEffect
  // writes AUTH_TOKEN to storage and immediately fires GET_PREFERENCES, all
  // before this React state has been updated. The fallback ensures that
  // in-flight request has a valid token while state catches up.
  const tokenProvider = () =>
    authToken ?? StorageService.get<string>(StorageKey.AUTH_TOKEN);
  httpClient.setTokenProvider(tokenProvider);

  const login = async (
    username: string,
    password: string,
    organization_code: string
  ): Promise<{ success: boolean; message?: string }> => {
    setIsLoading(true);

    try {
      const result = await execute({
        username,
        password,
        organization_code,
      });

      setIsLoading(false);

      if (result.code === ResponseCode.SUCCESS && result.data) {
        // Processor already returns correct format - use directly
        const user = result.data.user;
        const token = result.data.token;

        // Persist to storage and update in-memory state.
        // In-memory state is served to HttpClient via the registered token provider.
        StorageService.set(StorageKey.USER, user);
        StorageService.set(StorageKey.AUTH_TOKEN, token.access_token);
        StorageService.set(StorageKey.SESSION_METADATA, {
          login_time: new Date().toISOString(),
          last_activity: new Date().toISOString(),
          expiry: null,
        });

        setUser(user);
        setAuthToken(token.access_token);
        return { success: true };
      }

      // Map error codes to user-friendly messages
      const errorMessages: Record<string, string> = {
        INVALID_CREDENTIALS: 'Invalid username, password, or organization code',
        VALIDATION_FAILED: 'Please check your input and try again',
        OPERATION_FAILED: 'Login failed. Please try again',
      };

      return {
        success: false,
        message: errorMessages[result.code] || 'Login failed. Please try again',
      };
    } catch (error) {
      logger.auth.error('Login failed', error);
      setIsLoading(false);
      return {
        success: false,
        message: 'An unexpected error occurred',
      };
    }
  };

  const logout = () => {
    StorageService.clearAll();
    window.location.href = '/';
  };

  // Register logout as the unauthorized handler — same method called when
  // the server returns HTTP 401 (JWT expired/invalid).
  httpClient.setUnauthorizedHandler(logout);

  // Cross-tab auth sync: when another tab logs out or refreshes the token,
  // mirror that change here so all tabs stay in sync.
  useEffect(() => {
    const handleStorage = (e: StorageEvent) => {
      if (e.key === StorageKey.AUTH_TOKEN) {
        if (!e.newValue) {
          // Token cleared in another tab → logout this tab
          setUser(null);
          setAuthToken(null);
          window.location.href = '/';
        } else if (e.newValue !== authToken) {
          // Token updated in another tab → sync
          setAuthToken(e.newValue);
        }
      }
      if (e.key === StorageKey.USER && e.newValue) {
        setUser(JSON.parse(e.newValue));
      }
    };
    window.addEventListener('storage', handleStorage);
    return () => window.removeEventListener('storage', handleStorage);
  }, [authToken]);

  const updateUser = (updates: Partial<AuthUser>) => {
    if (!user) return;
    const updatedUser = { ...user, ...updates };
    setUser(updatedUser); // Triggers re-renders
    StorageService.set(StorageKey.USER, updatedUser); // Persists to localStorage
  };

  const value: AuthContextType = {
    user,
    login,
    logout,
    updateUser,
    isLoading,
    isAuthenticated: !!user,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

// eslint-disable-next-line react-refresh/only-export-components -- context+hook co-location pattern
export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
